import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { ClipboardList, CheckCircle2, Circle, BellRing, Search, ExternalLink } from "lucide-react";

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";
const isRecent = (iso) => !!iso && Date.now() - new Date(iso).getTime() < 48 * 3600 * 1000;

function Meter({ label, done, total, url, kind, onRemind, busy }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  const pending = total - done;
  return (
    <div className="border-2 border-[#0A0A0A] bg-white p-5" data-testid={`survey-board-${kind}-card`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow">{kind === "opening" ? "Opening" : "Exit"} survey</div>
          <div className="font-display text-3xl font-black tracking-tighter mt-1">{done}<span className="text-[#525252] text-xl font-bold"> / {total}</span></div>
          <div className="text-xs text-[#525252]">{label} · {pct}% complete</div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <Button onClick={() => onRemind(kind)} disabled={busy || pending === 0 || !url}
            className="rounded-none bg-[#0F1F2E] hover:bg-[#1a2f42] h-9" data-testid={`survey-board-${kind}-remind-btn`}>
            <BellRing size={14} className="mr-2" /> Remind {pending} who haven&apos;t
          </Button>
          {!url && <a href={`/survey/${kind}`} className="text-[11px] text-[#A23232] underline" data-testid={`survey-board-${kind}-no-url`}>Set the survey link first →</a>}
          {url && <a href={url} target="_blank" rel="noreferrer" className="text-[11px] text-[#525252] inline-flex items-center gap-1 hover:underline"><ExternalLink size={10} /> Open survey</a>}
        </div>
      </div>
      <div className="h-2 bg-[#F3F4F6] mt-4"><div className="h-full bg-[#25AAAA]" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function AdminSurveyBoard() {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/survey-completion").then(({ data }) => setData(data)).catch(() => toast.error("Could not load"));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return data.students.filter((r) => {
      if (filter === "opening_pending" && r.opening_done) return false;
      if (filter === "exit_pending" && r.exit_done) return false;
      return !s || (r.name || "").toLowerCase().includes(s) || (r.email || "").toLowerCase().includes(s) || (r.team_name || "").toLowerCase().includes(s);
    });
  }, [data, q, filter]);

  const remind = async (kind, onlyIds, lastNudged) => {
    const label = kind === "opening" ? data.opening.label : data.exit.label;
    const pendingRows = data.students.filter((r) => !r[`${kind}_done`]);
    const n = onlyIds ? onlyIds.length : pendingRows.length;
    const recent = onlyIds
      ? (lastNudged && isRecent(lastNudged) ? 1 : 0)
      : pendingRows.filter((r) => isRecent(r[`${kind}_nudged_at`])).length;
    const warn = recent > 0 ? ` ${recent === 1 && onlyIds ? "This student was" : `${recent} of them were`} already nudged in the last 48 hours.` : "";
    if (!(await confirm({
      title: `Send a ${label} reminder to ${n} student${n === 1 ? "" : "s"}?`,
      description: `Each gets an in-app notification and an email with the survey link.${warn}`,
      confirmText: recent > 0 ? "Send anyway" : "Send reminder",
      destructive: recent > 0,
    }))) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/survey-reminder/${kind}`, { only_user_ids: onlyIds || undefined });
      toast.success(`Reminded ${r.sent} · ${r.queued} email${r.queued === 1 ? "" : "s"} queued — they go out over the next few minutes`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="text-sm text-[#525252]">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="admin-survey-board">
      <div>
        <div className="label-eyebrow flex items-center gap-2"><ClipboardList size={12} /> Admin · surveys</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Survey completion</h1>
        <p className="text-[#525252] mt-2 max-w-2xl text-sm">Who has ticked off the opening and exit surveys. Students self-report by flipping the toggle on their survey page; nudge the rest with one click. Only students on the <strong>live Student Roster</strong> are listed and emailed — anyone removed from the roster is excluded automatically.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Meter kind="opening" {...data.opening} onRemind={(k) => remind(k)} busy={busy} />
        <Meter kind="exit" {...data.exit} onRemind={(k) => remind(k)} busy={busy} />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or team…" className="rounded-none h-10 pl-9" data-testid="survey-board-search" />
        </div>
        {[["all", "Everyone"], ["opening_pending", "Opening not done"], ["exit_pending", "Exit not done"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`text-xs px-3 py-2 border ${filter === k ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB] hover:border-[#0A0A0A]"}`}
            data-testid={`survey-board-filter-${k}`}>{l}</button>
        ))}
        <span className="text-xs text-[#525252] ml-auto">{rows.length} shown</span>
      </div>

      <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="survey-board-table">
          <thead className="bg-[#F3F4F6] text-[10px] uppercase tracking-[0.16em] text-[#525252]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Student</th>
              <th className="text-left px-4 py-3 font-semibold">Team</th>
              <th className="text-left px-4 py-3 font-semibold">Opening</th>
              <th className="text-left px-4 py-3 font-semibold">Exit</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`survey-board-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#0F1F2E]">{r.name}</div>
                  <div className="text-[11px] text-[#525252]">{r.email}</div>
                </td>
                <td className="px-4 py-3 text-[#525252]">{r.team_name || <span className="italic">No team</span>}</td>
                {[["opening", r.opening_done, r.opening_at, r.opening_nudged_at, r.opening_nudges], ["exit", r.exit_done, r.exit_at, r.exit_nudged_at, r.exit_nudges]].map(([k, done, at, nudgedAt, nudges]) => (
                  <td key={k} className="px-4 py-3">
                    {done
                      ? <span className="inline-flex items-center gap-1.5 text-[#0F5A5A] font-semibold text-xs" data-testid={`survey-board-${k}-done-${r.id}`}><CheckCircle2 size={14} /> Done {at && <span className="text-[#525252] font-normal">· {fmt(at)}</span>}</span>
                      : (
                        <span className="inline-flex flex-col gap-0.5">
                          <span className="inline-flex items-center gap-1.5 text-[#A23232] text-xs" data-testid={`survey-board-${k}-pending-${r.id}`}><Circle size={14} /> Not yet</span>
                          {nudgedAt && (
                            <span className={`text-[10px] ${isRecent(nudgedAt) ? "text-amber-700 font-semibold" : "text-[#525252]"}`} data-testid={`survey-board-${k}-nudged-${r.id}`}>
                              nudged {fmt(nudgedAt)}{nudges > 1 ? ` · ×${nudges}` : ""}
                            </span>
                          )}
                        </span>
                      )}
                  </td>
                ))}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {!r.opening_done && data.opening.url && (
                    <button onClick={() => remind("opening", [r.id], r.opening_nudged_at)} disabled={busy} className="text-[11px] uppercase tracking-wider text-[#0F1F2E] hover:underline mr-3" data-testid={`survey-board-remind-opening-${r.id}`}>Nudge opening</button>
                  )}
                  {!r.exit_done && data.exit.url && (
                    <button onClick={() => remind("exit", [r.id], r.exit_nudged_at)} disabled={busy} className="text-[11px] uppercase tracking-wider text-[#0F1F2E] hover:underline" data-testid={`survey-board-remind-exit-${r.id}`}>Nudge exit</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[#525252] italic">No students match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
